// import { sql, BuildColumns } from 'drizzle-orm';
// import {
//   uuid,
//   timestamp,
//   bigint,
//   varchar,
//   index,
//   pgTable,
//   PgColumnBuilderBase,
//   PgTableExtraConfig,
// } from 'drizzle-orm/pg-core';
//
// export function createPgTable<
//   TTableName extends string,
//   TColumnsMap extends Record<string, PgColumnBuilderBase>,
// >(
//   name: TTableName,
//   additionalColumns: TColumnsMap,
//   extraConfig?: (
//     self: BuildColumns<TTableName, TColumnsMap, 'pg'>,
//   ) => PgTableExtraConfig,
// ) {
//   // Define base columns
//   const baseColumns = {
//     id: uuid('id')
//       .primaryKey()
//       .default(sql`gen_random_uuid()`),
//     versionId: uuid('versionId')
//       .notNull()
//       .unique()
//       .default(sql`gen_random_uuid()`),
//     createdAtTs: timestamp('createdAtTs', { withTimezone: true })
//       .notNull()
//       .defaultNow(),
//     updatedAtTs: timestamp('updatedAtTs', { withTimezone: true })
//       .notNull()
//       .defaultNow(),
//     versionNumber: bigint('versionNumber', { mode: 'number' })
//       .notNull()
//       .default(1),
//     tenantId: uuid('tenantId').notNull(),
//     archivedAtTs: timestamp('archivedAtTs', { withTimezone: true }),
//     lastModifiedBy: uuid('lastModifiedBy'),
//     createdBy: uuid('createdBy'),
//     legacySystemId: varchar('legacySystemId', { length: 255 }),
//   };
//
//   // Merge base columns with additional columns
//   const columns = {
//     ...baseColumns,
//     ...additionalColumns,
//   };
//
//   // Call the original pgTable function with indexes if provided
//   return pgTable<TTableName, typeof columns>(name, columns, (self) => {
//     const extra = extraConfig?.(self) ?? {};
//     return {
//       baseTableTemplateCreatedAtTs: index(
//         'base_table_template_created_at_ts',
//       ).on(self.createdAtTs),
//       baseTableTemplateUpdatedAtTs: index(
//         'base_table_template_updated_at_ts',
//       ).on(self.updatedAtTs),
//       baseTableTemplateUpdatedAtTsDesc: index(
//         'base_table_template_updated_at_ts_desc',
//       )
//         .on(self.updatedAtTs)
//         .desc(),
//       baseTableTemplateTenantId: index('base_table_template_tenantid').on(
//         self.tenantId,
//       ),
//       baseTableTemplateVersionNumber: index(
//         'base_table_template_version_number',
//       ).on(self.versionNumber),
//       ...extra,
//     };
//   });
// }

import { customType, pgEnum } from 'drizzle-orm/pg-core';
import currency, { default as Currency } from 'currency.js';
import { Decimal } from 'decimal.js';
import * as wkx from 'wkx';

export const tstzrange = customType<{ data: string }>({
  dataType() {
    return 'tstzrange';
  },
});

export const numrange = customType<{ data: string }>({
  dataType() {
    return 'numrange';
  },
});

export const money = customType<{ data: number }>({
  dataType() {
    return 'money';
  },
});

export const customer_category = pgEnum('customer_category', [
  'RESIDENTIAL',
  'COMMERCIAL',
]);

export const tax_status = pgEnum('tax_status', ['TAXABLE', 'NON_TAXABLE']);

export const payment_method_type = pgEnum('payment_method_type', [
  'CASH',
  'CREDIT_CARD',
  'ACH',
  'CHECK',
  'CREDIT_BALANCE',
]);

export const container_type = pgEnum('container_type', [
  'DUMPSTER',
  'LIQUID_WASTE_CONTAINER',
  'FRONT_LOAD',
  'CART',
]);

export const workflow_kind = pgEnum('workflow_kind', [
  'DELIVERY',
  'DUMP',
  'EXCHANGE_SAME',
  'DUMP_AND_RETURN',
  'PICKUP',
  'LIVELOAD',
  'CUSTOM',
  'UNKNOWN',
  'DRY_RUN',
  'SERVICE_LIQUID_WASTE',
  'RESPOT',
  'MIGRATE',
]);

export const currencyType = pgEnum('currency', ['usd', 'cad']);

export const currencyCents = customType<{
  data: Currency;
  driverData: string;
  config: { precision?: number; scale?: number };
}>({
  dataType(config) {
    if (config?.precision && config?.scale) {
      return `numeric(${config.precision}, ${config.scale})`;
    } else if (config?.precision) {
      return `numeric(${config.precision})`;
    } else {
      return 'numeric';
    }
  },
  fromDriver(value: string): Currency {
    return currency(value, { fromCents: true });
  },
});

export const decimal = customType<{
  data: Decimal;
  driverData: string;
  config: { precision?: number; scale?: number };
}>({
  dataType(config) {
    if (config?.precision && config?.scale) {
      return `numeric(${config.precision}, ${config.scale})`;
    } else if (config?.precision) {
      return `numeric(${config.precision})`;
    } else {
      return 'numeric';
    }
  },
  toDriver(value: Decimal): string {
    return value.toString();
  },
  fromDriver(value: string): Decimal {
    return new Decimal(value);
  },
});

export const isoTimestamp = customType<{
  data: string;
  driverData: string;
  config: { withTimezone: boolean; precision?: number };
}>({
  dataType(config) {
    const precision =
      typeof config?.precision !== 'undefined' ? ` (${config.precision})` : '';
    return `timestamp${precision}${
      config?.withTimezone ? ' with time zone' : ''
    }`;
  },
  fromDriver(value: string): string {
    return new Date(value).toISOString();
  },
});

export const isoTimestampDate = customType<{
  data: Date;
  driverData: string;
  config: { withTimezone: boolean; precision?: number };
}>({
  dataType(config) {
    const precision =
      typeof config?.precision !== 'undefined' ? ` (${config.precision})` : '';
    return `timestamp${precision}${
      config?.withTimezone ? ' with time zone' : ''
    }`;
  },
  fromDriver(value: string): Date {
    return new Date(value);
  },
});

// Add Geometry type for PostGIS spatial data
type Coordinate = [number, number];
type GeoJsonMultiPolygon = {
  type: string;
  coordinates: Coordinate[][][];
};

/**
 * Custom type for PostGIS MultiPolygon
 * Uses SRID 4326 for lat/long coordinates
 * 
 * This implementation handles both WKB and WKT formats from PostGIS
 */
export const postgisMultiPolygon = customType<{
  data: Coordinate[][][];
  driverData: string;
}>({
  dataType() {
    return 'geometry(MultiPolygon, 4326)';
  },
  toDriver(coordinates: Coordinate[][][]): string {
    // Convert GeoJSON-like structure to WKT using wkx library
    const polygons = coordinates.map(polygonCoords => {
      // For each polygon, create exterior ring and interior rings
      const exteriorRing = polygonCoords[0].map(coord => 
        new wkx.Point(coord[0], coord[1])
      );
      
      // Any additional rings are interior rings (holes)
      const interiorRings = polygonCoords.slice(1).map(ring => 
        ring.map(coord => new wkx.Point(coord[0], coord[1]))
      );
      
      return new wkx.Polygon(exteriorRing, interiorRings);
    });
    
    // Create a MultiPolygon from the polygons
    const multiPolygon = new wkx.MultiPolygon(polygons, 4326);
    
    // Convert to WKT format for PostgreSQL
    return multiPolygon.toWkt();
  },
  fromDriver(data: string): Coordinate[][][] {
    try {
      // Check if the data appears to be WKB (hex format) or WKT (text format)
      const isWkt = data.startsWith('SRID=') || 
                    data.startsWith('MULTIPOLYGON') || 
                    data.startsWith('POLYGON') || 
                    data.startsWith('POINT');
      
      // Parse the geometry data using wkx
      let geometry: any;
      
      if (isWkt) {
        // Parse WKT string
        geometry = wkx.Geometry.parse(data);
      } else {
        // Create a Buffer from the WKB data if it's in hex string format
        try {
          // Parse the WKB using wkx
          const wkbBuffer = Buffer.from(data, 'hex');
          geometry = wkx.Geometry.parse(wkbBuffer);
        } catch (error) {
          console.error('Error parsing WKB data:', error);
          return [];
        }
      }
      
      // Check if the result is a MultiPolygon
      if (geometry instanceof wkx.MultiPolygon) {
        // Return the coordinates in the expected format
        return geometry.polygons.map((polygon: any) => {
          // Extract exterior ring
          const exteriorRing = polygon.exteriorRing.map((point: any) => 
            [point.x, point.y]
          );
          
          // Extract interior rings (holes)
          const interiorRings = polygon.interiorRings.map((ring: any) => 
            ring.map((point: any) => [point.x, point.y])
          );
          
          // Combine exterior ring and interior rings
          return [exteriorRing, ...interiorRings];
        });
      } else if (geometry instanceof wkx.Polygon) {
        // If we got a single Polygon, wrap it in a MultiPolygon-like structure
        const exteriorRing = geometry.exteriorRing.map((point: any) => 
          [point.x, point.y]
        );
        
        const interiorRings = geometry.interiorRings.map((ring: any) => 
          ring.map((point: any) => [point.x, point.y])
        );
        
        return [[exteriorRing, ...interiorRings]];
      }
      
      console.warn('Parsed geometry is neither a MultiPolygon nor a Polygon');
      return [];
    } catch (error) {
      console.error('Error parsing PostGIS MultiPolygon data:', error);
      return [];
    }
  },
});
